import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Images, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { FileGallery } from "@/components/FileGallery";
import { compressImage, getPosition } from "@/lib/helpers";
import { t } from "@/lib/i18n";

const KINDS = ["cmr", "lieferschein", "other"];

export function DocUploader({ order, lang, onUploaded }) {
  const [kind, setKind] = useState("cmr");
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);
  const galRef = useRef(null);
  const docs = order.files.filter((f) => KINDS.includes(f.kind));

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const [pos, ...compressed] = await Promise.all([getPosition(), ...files.map((f) => compressImage(f))]);
      const fd = new FormData();
      compressed.forEach((f) => fd.append("files", f));
      fd.append("kind", kind);
      if (pos) {
        fd.append("lat", pos.lat);
        fd.append("lng", pos.lng);
      }
      await api.post(`/driver/orders/${order.id}/photos`, fd);
      toast.success(t(lang, "uploaded"));
      onUploaded();
    } catch (e) {
      toast.error(errMsg(e, t(lang, "error")));
    } finally {
      setBusy(false);
      if (camRef.current) camRef.current.value = "";
      if (galRef.current) galRef.current.value = "";
    }
  };

  return (
    <div className="panel space-y-4" data-testid="doc-uploader">
      <div className="eyebrow">{t(lang, "documents")}</div>
      <div className="flex gap-1 rounded-xl border border-white/10 bg-black/30 p-1">
        {KINDS.map((k) => (
          <button key={k} type="button" onClick={() => setKind(k)} data-testid={`doc-kind-${k}`}
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${kind === k ? "bg-blue-600 text-white" : "text-slate-400"}`}>
            {t(lang, k)}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" disabled={busy} onClick={() => camRef.current?.click()} data-testid="take-photo-btn"
          className="flex h-14 items-center justify-center gap-2 rounded-xl bg-[#1E293B] font-semibold active:bg-[#334155] disabled:opacity-60">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />} {busy ? t(lang, "uploading") : t(lang, "take_photo")}
        </button>
        <button type="button" disabled={busy} onClick={() => galRef.current?.click()} data-testid="gallery-photo-btn"
          className="flex h-14 items-center justify-center gap-2 rounded-xl bg-[#1E293B] font-semibold active:bg-[#334155] disabled:opacity-60">
          <Images className="h-5 w-5" /> {t(lang, "from_gallery")}
        </button>
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" hidden data-testid="camera-file-input" onChange={(e) => upload(e.target.files)} />
      <input ref={galRef} type="file" accept="image/*,application/pdf" multiple hidden data-testid="gallery-file-input" onChange={(e) => upload(e.target.files)} />
      <FileGallery files={docs} empty={t(lang, "no_docs")} />
    </div>
  );
}
